import {
  getSourceItemParentQuantity,
  isSourceItemReturned,
  mapSourceItemTaxes,
} from "../../../shared/utility";

const QUANTITY_EPSILON = 0.0001;

function priceKey(price) {
  return Number(price).toFixed(4);
}

function isReturnedCarton(carton) {
  return (
    Number(carton.is_returned) === 1 ||
    parseInt(carton.credit_return_id, 10) > 0
  );
}

function cartonPricing(sourceItem) {
  const parentQuantity = getSourceItemParentQuantity(sourceItem);
  const segments = (sourceItem.price_splits || [])
    .map((split) => ({
      id: Number(split.id) || 0,
      quantity: parseFloat(split.quantity),
      // Edit the saved manual prices, not the promotion's charged average.
      unitPrice: parseFloat(split.base_unit_price ?? split.unit_price),
    }))
    .filter(
      (split) =>
        Number.isFinite(split.quantity) && Number.isFinite(split.unitPrice),
    )
    .sort((left, right) => left.id - right.id);

  if (segments.length === 0) {
    return { kind: "unpriced", parentQuantity, segments: [] };
  }

  const covered = segments.reduce((sum, split) => sum + split.quantity, 0);
  if (Math.abs(covered - parentQuantity) > QUANTITY_EPSILON) {
    return { kind: "partial", parentQuantity, segments: [] };
  }

  return {
    kind: "priced",
    parentQuantity,
    unitPrice: segments[0].unitPrice,
    segments,
  };
}

function reconstructQuantityTiers(entries, savedBaseUnitPrice = null) {
  if (entries.length === 0) {
    return { baseUnitPrice: null, tiers: [], hasPartialSplits: false };
  }

  if (entries.some((entry) => entry.kind === "partial")) {
    return { baseUnitPrice: null, tiers: [], hasPartialSplits: true };
  }

  if (entries.every((entry) => entry.kind === "unpriced")) {
    return { baseUnitPrice: null, tiers: [], hasPartialSplits: false };
  }

  if (entries.some((entry) => entry.kind === "unpriced")) {
    return { baseUnitPrice: null, tiers: [], hasPartialSplits: true };
  }

  const segments = entries.flatMap((entry) => entry.segments || []);
  const baseUnitPrice = savedBaseUnitPrice ?? segments[0].unitPrice;
  const baseKey = priceKey(baseUnitPrice);
  const splitAt = segments.findIndex(
    (segment) => priceKey(segment.unitPrice) !== baseKey,
  );

  if (splitAt === -1) {
    return {
      baseUnitPrice,
      tiers: [],
      hasPartialSplits: false,
    };
  }

  const suffix = segments.slice(splitAt);
  if (suffix.some((segment) => priceKey(segment.unitPrice) === baseKey)) {
    return { baseUnitPrice: null, tiers: [], hasPartialSplits: true };
  }

  const tiers = [];
  let index = 0;
  while (index < suffix.length) {
    const key = priceKey(suffix[index].unitPrice);
    let quantity = 0;
    let cursor = index;
    while (cursor < suffix.length && priceKey(suffix[cursor].unitPrice) === key) {
      quantity += suffix[cursor].quantity;
      cursor += 1;
    }
    tiers.push({ quantity, unitPrice: suffix[index].unitPrice });
    index = cursor;
  }

  return {
    baseUnitPrice,
    tiers,
    hasPartialSplits: false,
  };
}

function productIdFor(sourceItem) {
  const id = sourceItem.manufactured_product?.product_id ?? sourceItem.manufactured_product?.product?.id;
  return id == null ? null : Number(id);
}

function ensureGroup(groups, sourceItem, useProductIdentity) {
  const plu = String(sourceItem.manufactured_product?.plu ?? "");
  const sellingUnit = parseInt(sourceItem.selling_unit_of_measure, 10);
  const productId = productIdFor(sourceItem);
  const usesProductIdentity = useProductIdentity && productId !== null;
  const identity = usesProductIdentity ? `product:${productId}` : `plu:${plu}`;
  const key = `${identity}|${Number.isNaN(sellingUnit) ? "" : sellingUnit}`;

  if (!groups.has(key)) {
    groups.set(key, {
      key,
      plu,
      productId,
      usesProductIdentity,
      sellingUnit,
      productName: sourceItem.manufactured_product?.product?.name || "",
      cartons: [],
      mixedItems: [],
      taxItems: [],
      sourceItems: [],
    });
  }

  const group = groups.get(key);
  if (!group.productName) {
    group.productName = sourceItem.manufactured_product?.product?.name || "";
  }
  return group;
}

function taxRateIdsFor(sourceItem) {
  return mapSourceItemTaxes(sourceItem)
    .map((tax) => Number(tax.value))
    .filter((id) => Number.isInteger(id))
    .sort((left, right) => left - right);
}

function rememberTaxes(group, sourceItem) {
  const itemPlu = String(sourceItem.manufactured_product?.plu ?? "");
  const itemUnit = parseInt(sourceItem.selling_unit_of_measure, 10);
  const matchesProduct = group.usesProductIdentity
    ? productIdFor(sourceItem) === group.productId
    : itemPlu === group.plu;
  if (!matchesProduct || itemUnit !== group.sellingUnit) {
    return;
  }

  group.taxItems.push({
    sourceItemId: sourceItem.id,
    taxRateIds: taxRateIdsFor(sourceItem),
    taxes: mapSourceItemTaxes(sourceItem),
  });
}

function rememberSourceItem(group, sourceItem) {
  const itemPlu = String(sourceItem.manufactured_product?.plu ?? "");
  const itemUnit = parseInt(sourceItem.selling_unit_of_measure, 10);
  const matchesProduct = group.usesProductIdentity
    ? productIdFor(sourceItem) === group.productId
    : itemPlu === group.plu;
  if (!matchesProduct || itemUnit !== group.sellingUnit || isSourceItemReturned(sourceItem)) {
    return;
  }
  if (!group.sourceItems.some((item) => String(item.id) === String(sourceItem.id))) {
    group.sourceItems.push(sourceItem);
  }
}

function hasPositiveQuantity(sourceItem) {
  return getSourceItemParentQuantity(sourceItem) > QUANTITY_EPSILON;
}

function addPricedLine(groups, carton, sourceItem, useProductIdentity, asFullCarton) {
  const group = ensureGroup(groups, sourceItem, useProductIdentity);
  const entry = pricedEntry(carton, sourceItem, cartonPricing(sourceItem));
  if (asFullCarton) {
    group.cartons.push(entry);
  } else {
    group.mixedItems.push(entry);
  }
  rememberTaxes(group, sourceItem);
  rememberSourceItem(group, sourceItem);
}

function pricedEntry(carton, sourceItem, pricing) {
  return {
    mixedCartonId: Number(carton.id),
    sourceItemId: sourceItem.id,
    lotNumber: sourceItem.manufactured_product?.lot_number || "",
    subtotalPrice: parseFloat(sourceItem.subtotal_price || 0) || 0,
    ...pricing,
  };
}

export function buildPluPriceGroups(mixedCartons, promotionPricingGroups = [], promotionPricingEnabled = false) {
  const groups = new Map();
  const useProductIdentity = Boolean(promotionPricingEnabled) || promotionPricingGroups.length > 0;

  (mixedCartons || []).forEach((carton) => {
    if (isReturnedCarton(carton)) {
      return;
    }

    const positiveItems = (carton.source_items || []).filter(hasPositiveQuantity);
    if (positiveItems.length === 0) {
      return;
    }

    const isSingleFullCarton =
      Number(carton.is_full_carton) === 1 && positiveItems.length === 1;
    if (isSingleFullCarton) {
      addPricedLine(groups, carton, positiveItems[0], useProductIdentity, true);
      return;
    }

    positiveItems.forEach((sourceItem) => {
      addPricedLine(groups, carton, sourceItem, useProductIdentity, false);
    });
  });

  return Array.from(groups.values())
    .map((group) => {
      group.cartons.sort(
        (left, right) => left.mixedCartonId - right.mixedCartonId,
      );
      group.mixedItems.sort(
        (left, right) =>
          left.mixedCartonId - right.mixedCartonId ||
          Number(left.sourceItemId) - Number(right.sourceItemId),
      );
      const pricingMetadata = group.productId === null ? null : promotionPricingGroups.find(
        (item) => Number(item.product_id) === Number(group.productId) &&
          Number(item.selling_unit_of_measure) === Number(group.sellingUnit),
      );
      const reconstruction = reconstructQuantityTiers([
        ...group.cartons,
        ...group.mixedItems,
      ], pricingMetadata?.base_unit_price);
      const taxSignatures = group.taxItems.map((item) =>
        item.taxRateIds.join("|"),
      );
      const taxesDiffer =
        taxSignatures.length > 0 &&
        taxSignatures.some((signature) => signature !== taxSignatures[0]);
      const taxes = taxesDiffer ? [] : group.taxItems[0]?.taxes ?? [];
      const taxRateIds = taxesDiffer ? [] : group.taxItems[0]?.taxRateIds ?? [];
      delete group.taxItems;

      return {
        ...group,
        promotionPricing: pricingMetadata || null,
        ...reconstruction,
        taxes,
        taxRateIds,
        taxesDiffer,
        cartonCount: new Set(
          [...group.cartons, ...group.mixedItems].map((item) => item.mixedCartonId),
        ).size,
        totalQuantity:
          group.cartons.reduce((sum, carton) => sum + carton.parentQuantity, 0) +
          group.mixedItems.reduce((sum, item) => sum + item.parentQuantity, 0),
        subtotalPrice:
          group.cartons.reduce(
            (sum, carton) => sum + (Number(carton.subtotalPrice) || 0),
            0,
          ) +
          group.mixedItems.reduce(
            (sum, item) => sum + (Number(item.subtotalPrice) || 0),
            0,
          ),
        lots: [
          ...new Set(
            [...group.cartons, ...group.mixedItems]
              .map((item) => item.lotNumber)
              .filter(Boolean),
          ),
        ],
      };
    })
    .sort(
      (left, right) =>
        left.productName.localeCompare(right.productName) ||
        left.plu.localeCompare(right.plu) ||
        left.sellingUnit - right.sellingUnit,
    );
}

export function parsePriceDraft(totalQuantity, unitPriceRaw, tiers) {
  if (unitPriceRaw === "" || unitPriceRaw == null) {
    return { ok: false, message: "Enter a unit price." };
  }

  const unitPrice = Number(unitPriceRaw);
  if (!Number.isFinite(unitPrice) || unitPrice < 0) {
    return { ok: false, message: "Unit price cannot be negative." };
  }

  const parsedTiers = [];
  for (const tier of tiers || []) {
    const quantity = Number(tier.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      return {
        ok: false,
        message: "Split quantity must be greater than zero.",
      };
    }

    if (tier.unitPrice === "" || tier.unitPrice == null) {
      return { ok: false, message: "Enter a unit price." };
    }

    const tierPrice = Number(tier.unitPrice);
    if (!Number.isFinite(tierPrice) || tierPrice < 0) {
      return { ok: false, message: "Unit price cannot be negative." };
    }

    parsedTiers.push({ quantity, unit_price: tierPrice });
  }

  const claimed = parsedTiers.reduce((sum, tier) => sum + tier.quantity, 0);
  if (claimed - totalQuantity > QUANTITY_EPSILON) {
    return {
      ok: false,
      message: "Extra tier quantity cannot exceed the PLU group.",
    };
  }

  return { ok: true, unitPrice, tiers: parsedTiers };
}

export function estimateGroupSubtotal(totalQuantity, unitPriceRaw, tiers) {
  const parsed = parsePriceDraft(totalQuantity, unitPriceRaw, tiers);
  if (!parsed.ok) {
    return null;
  }

  const claimed = parsed.tiers.reduce((sum, tier) => sum + tier.quantity, 0);
  const baseQuantity = totalQuantity - claimed;

  return (
    baseQuantity * parsed.unitPrice +
    parsed.tiers.reduce(
      (sum, tier) => sum + tier.quantity * tier.unit_price,
      0,
    )
  );
}
